// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { ATL_PAGE_CONTENT_FOOTER_ACTIONS } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { WebPanelLocationQuery } from '@confluence/web-panel-location/entry-points/WebPanelLocationQuery.ts';
import type {
	WebPanelLocationQueryData,
	WebPanelLocationQueryVariables,
} from '@confluence/web-panel-location/entry-points/WebPanelLocationQueryArtifacts.ts';
import { usePathnameMatcher } from '@confluence/route-manager/entry-points/RouteState.ts';

export type ReactionsSettings = {
	isReactionsEnabled: boolean;
	/**
	 * True only once the site setting has been resolved and reactions are turned off.
	 * Stays false while loading, on error, and for PDF export so reactions are never
	 * hidden based on an unknown setting.
	 */
	isReactionsDisabledForSite: boolean;
};

type WebPanelLocationQueryResult = {
	data?: WebPanelLocationQueryData | null;
	loading?: boolean;
	error?: unknown;
};

export const useReactionsSettings = (
	contentId: string,
	{ skip = false }: { skip?: boolean } = {},
): ReactionsSettings => {
	const isPDFExport = usePathnameMatcher({
		selector: (pathname) => pathname?.includes('/wiki/pdf/') ?? false,
	});

	const baseOptions = {
		variables: {
			contentId,
			location: ATL_PAGE_CONTENT_FOOTER_ACTIONS,
		},
		skip: isPDFExport || skip,
	};

	const queryResult: WebPanelLocationQueryResult =
		// eslint-disable-next-line graphql-client-usage/graphql-generic-types
		useQueryNoFurtherUpdateInterop<WebPanelLocationQueryData, WebPanelLocationQueryVariables>(
			WebPanelLocationQuery,
			baseOptions,
		);
	const webPanels = queryResult.data?.webPanels;

	const isReactionsEnabled = !isPDFExport && Boolean(webPanels?.length);
	const isReactionsDisabledForSite =
		!isPDFExport &&
		!skip &&
		!queryResult.loading &&
		!queryResult.error &&
		Array.isArray(webPanels) &&
		webPanels.length === 0;

	return { isReactionsEnabled, isReactionsDisabledForSite };
};

export const useIsReactionsEnabled = (contentId: string): boolean =>
	useReactionsSettings(contentId).isReactionsEnabled;
